import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AUTH from "../api/services/AUTH";
import Spinner from "../components/spinner";
import OTPInput from "otp-input-react";
import { paths } from "../routes/paths";
import styles from "../styles/_screens/login.module.scss";

import { BASE_URL } from "../config";

const Login = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // ── SSO from registration site ──
  // Accepts ?ssoToken=...&userId=...&userType=... and signs in automatically
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("ssoToken");
    const ssoUserId = params.get("userId");
    const ssoUserType = params.get("userType");

    if (ssoToken && ssoUserId) {
      localStorage.setItem("token", JSON.stringify(ssoToken));
      localStorage.setItem("user_id", JSON.stringify(ssoUserId));
      localStorage.setItem("user_type", JSON.stringify(ssoUserType ? Number(ssoUserType) : 1));
      navigate(paths.appointments, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (token) navigate(paths.appointments);
  }, [token]);

  // ── View mode: "login" | "otp" | "register" | "registerOtp" ──
  const [view, setView] = useState("login");

  // ── Login state ──
  const [phone, setPhone] = useState("");
  const [OTP, setOTP] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Register state ──
  const [regBusinessName, setRegBusinessName] = useState("");
  const [regOwnerName, setRegOwnerName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regAddress, setRegAddress] = useState("");
  const [regLatitude, setRegLatitude] = useState("");
  const [regLongitude, setRegLongitude] = useState("");
  const [regErrors, setRegErrors] = useState({});
  const [regLoading, setRegLoading] = useState(false);
  const [regOTP, setRegOTP] = useState("");
  const addressInputRef = useRef(null);

  // ── Google Places autocomplete ──
  useEffect(() => {
    if (view !== "register" || !addressInputRef.current) return;
    if (!window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ["geocode"], componentRestrictions: { country: "IL" } }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      setRegAddress(place.formatted_address || "");
      setRegLatitude(place.geometry?.location?.lat()?.toString() || "");
      setRegLongitude(place.geometry?.location?.lng()?.toString() || "");
    });

    return () => {
      if (addressInputRef.current) {
        window.google.maps.event.clearInstanceListeners(addressInputRef.current);
      }
    };
  }, [view]);

  // ── Login: send OTP ──
  const handleLogin = async (channel = "whatsapp") => {
    if (!phone) {
      toast.error("יש להזין מספר טלפון");
      return;
    }
    setLoading(true);
    try {
      const res = await AUTH.login({ phone, channel });
      if (res.status === 201) {
        toast.success(res.data.message);
        setView("otp");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה בהתחברות");
    }
    setLoading(false);
  };

  // ── Login: verify OTP ──
  const verifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await AUTH.verify({ phone, otp: OTP });
      toast.success(res.data.message);
      localStorage.setItem("token", JSON.stringify(res.data.token.access.token));
      localStorage.setItem("user_type", JSON.stringify(res.data.user.type));
      localStorage.setItem("user_id", JSON.stringify(res.data.user.id));
      navigate(paths.appointments);
    } catch (error) {
      toast.error("קוד OTP שגוי");
    }
    setLoading(false);
  };

  // ── Register: submit form → send OTP ──
  const handleRegister = async (e) => {
    e?.preventDefault();
    const errors = {};
    if (!regBusinessName) errors.businessName = "אנו צריכים לדעת את שם העסק :)";
    if (!regOwnerName) errors.ownerName = "נשמח לדעת עם מי יש לנו עסק 😅";
    if (!regEmail) errors.email = "כתובת מייל זהו שדה חובה";
    if (!regPhone) errors.phone = "כמעט שכחת להזין מספר טלפון";
    if (!regAddress) errors.address = "כתובת זהו שדה חובה";

    if (regPhone && !/^05\d{8}$/.test(regPhone)) {
      errors.phone = "מספר טלפון לא תקין (05XXXXXXXX)";
    }

    setRegErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setRegLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/business/login`, {
        phone: regPhone,
        business_phone: regPhone,
        channel: "whatsapp",
      });
      if (res.status === 201) {
        toast.success("קוד אימות נשלח בוואטסאפ!");
        setView("registerOtp");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה בהרשמה");
    }
    setRegLoading(false);
  };

  // ── Register: verify OTP → complete registration ──
  const verifyRegOTP = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/business/otp_verify`, {
        phone: regPhone,
        otp: regOTP,
      });

      const businessId = res.data.user.id;
      const authToken = res.data.token.access.token;

      // Update profile with registration data
      await axios.put(
        `${BASE_URL}/business/update_profile_info`,
        {
          id: businessId,
          email: regEmail,
          address: regAddress,
          latitude: regLatitude,
          longitude: regLongitude,
          business_name: regBusinessName,
          name: regOwnerName,
          phone: regPhone,
          first_time: true,
        },
        { headers: { Authorization: authToken } }
      );

      toast.success("העסק נרשם בהצלחה! 🎉");
      localStorage.setItem("token", JSON.stringify(authToken));
      localStorage.setItem("user_type", JSON.stringify(res.data.user.type));
      localStorage.setItem("user_id", JSON.stringify(businessId));
      navigate(paths.appointments);
    } catch (error) {
      toast.error(error.response?.data?.message || "קוד אימות שגוי");
    }
    setRegLoading(false);
  };

  // ── Render login form ──
  const renderLogin = () => (
    <>
      <h2 className={styles.formTitle}>התחברות לממשק הניהול</h2>
      <p className={styles.formDesc}>הזינו את מספר הטלפון שלכם כדי להתחבר</p>

      <div className={styles.field}>
        <label>מספר טלפון</label>
        <div className={styles.phoneInput}>
          <span className={styles.phonePrefix}>+972</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="050-1234567"
            autoFocus
          />
        </div>
      </div>

      <button
        type="button"
        className={styles.submitBtn}
        onClick={() => handleLogin("sms")}
        disabled={loading}
      >
        {loading ? <Spinner /> : "שלח קוד ב-SMS"}
      </button>

      <button
        type="button"
        className={styles.whatsappLink}
        onClick={() => handleLogin("whatsapp")}
        disabled={loading}
      >
        <img src="/assets/whatsapp-icon-1.svg" alt="WhatsApp" />
        <span>שלח קוד בוואטסאפ</span>
      </button>

      <div className={styles.divider}>
        <span>עסק חדש?</span>
      </div>

      <button
        type="button"
        className={styles.registerToggleBtn}
        onClick={() => setView("register")}
      >
        הרשמה לתור דיגיטל
      </button>
    </>
  );

  // ── Render OTP form ──
  const renderOTP = () => (
    <>
      <div className={styles.otpIcon}>📲</div>
      <h2 className={styles.formTitle}>קוד אימות נשלח!</h2>
      <p className={styles.formDesc}>הזינו את הקוד שנשלח אליכם</p>

      <form onSubmit={verifyOTP} className={styles.form}>
        <OTPInput
          value={OTP}
          onChange={setOTP}
          autoFocus
          OTPLength={4}
          otpType="number"
          disabled={false}
          className={styles.otp}
          style={{ justifyContent: "center", direction: "ltr" }}
          inputStyles={{
            margin: "0px 6px",
            width: "52px",
            height: "52px",
            fontSize: "24px",
            borderRadius: "12px",
            border: "2px solid #e2e8f0",
            fontWeight: "700",
          }}
        />

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? <Spinner /> : "אימות והתחברות"}
        </button>

        <button
          type="button"
          className={styles.backBtn}
          onClick={() => { setView("login"); setOTP(""); }}
        >
          חזרה
        </button>
      </form>
    </>
  );

  // ── Render register form ──
  const renderRegister = () => (
    <>
      <h2 className={styles.formTitle}>צור חשבון חדש</h2>
      <p className={styles.formDesc}>
        תוך 2 דקות תוכלו לאפשר ללקוחות שלכם לקבוע תור אונליין
      </p>

      <form className={styles.form} onSubmit={handleRegister}>
        <div className={styles.field}>
          <label>שם העסק <span className={styles.required}>*</span></label>
          <input
            type="text"
            placeholder="שם העסק"
            value={regBusinessName}
            onChange={(e) => setRegBusinessName(e.target.value)}
          />
          {regErrors.businessName && (
            <small className={styles.errorMsg}>{regErrors.businessName}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>השם שלך <span className={styles.required}>*</span></label>
          <input
            type="text"
            placeholder="שם מלא"
            value={regOwnerName}
            onChange={(e) => setRegOwnerName(e.target.value)}
          />
          {regErrors.ownerName && (
            <small className={styles.errorMsg}>{regErrors.ownerName}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>כתובת מייל <span className={styles.required}>*</span></label>
          <input
            type="email"
            placeholder="כתובת המייל שלך"
            value={regEmail}
            onChange={(e) => setRegEmail(e.target.value)}
          />
          {regErrors.email && (
            <small className={styles.errorMsg}>{regErrors.email}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>טלפון <span className={styles.required}>*</span></label>
          <input
            type="tel"
            placeholder="05XXXXXXXX"
            value={regPhone}
            onChange={(e) => setRegPhone(e.target.value)}
          />
          {regErrors.phone && (
            <small className={styles.errorMsg}>{regErrors.phone}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>
            כתובת של העסק <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            placeholder="כתובת עסק מלאה כולל עיר, רחוב ומספר"
            ref={addressInputRef}
            onChange={(e) => setRegAddress(e.target.value)}
          />
          <small className={styles.hintMsg}>
            נא להקליד ולבחור מהרשימה בלבד
          </small>
          {regErrors.address && (
            <small className={styles.errorMsg}>{regErrors.address}</small>
          )}
        </div>

        <button
          type="submit"
          className={styles.whatsappBtn}
          disabled={regLoading}
        >
          {regLoading ? (
            <Spinner />
          ) : (
            <>
              <img src="/assets/whatsapp-icon-1.svg" alt="WhatsApp" />
              <span>הרשמה בחינם</span>
            </>
          )}
        </button>
      </form>

      <button
        type="button"
        className={styles.backBtn}
        onClick={() => setView("login")}
        style={{ marginTop: 12 }}
      >
        כבר יש לי חשבון? התחברות
      </button>
    </>
  );

  // ── Render register OTP ──
  const renderRegisterOTP = () => (
    <>
      <div className={styles.otpIcon}>📲</div>
      <h2 className={styles.formTitle}>קוד אימות נשלח!</h2>
      <p className={styles.formDesc}>
        שלחנו קוד אימות בוואטסאפ למספר {regPhone}
      </p>

      <form onSubmit={verifyRegOTP} className={styles.form}>
        <OTPInput
          value={regOTP}
          onChange={setRegOTP}
          autoFocus
          OTPLength={4}
          otpType="number"
          disabled={false}
          className={styles.otp}
          style={{ justifyContent: "center", direction: "ltr" }}
          inputStyles={{
            margin: "0px 6px",
            width: "52px",
            height: "52px",
            fontSize: "24px",
            borderRadius: "12px",
            border: "2px solid #e2e8f0",
            fontWeight: "700",
          }}
        />

        <button type="submit" className={styles.submitBtn} disabled={regLoading}>
          {regLoading ? <Spinner /> : "אימות והרשמה"}
        </button>

        <button
          type="button"
          className={styles.backBtn}
          onClick={() => { setView("register"); setRegOTP(""); }}
        >
          חזרה
        </button>
      </form>
    </>
  );

  return (
    <div className={styles.page}>
      {/* ── Right side — form ── */}
      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          <img
            src="https://tor.digital/wp-content/themes/hello-elementor/hmassets/img/appicon.svg"
            alt="logo"
            className={styles.formLogo}
          />

          {view === "login" && renderLogin()}
          {view === "otp" && renderOTP()}
          {view === "register" && renderRegister()}
          {view === "registerOtp" && renderRegisterOTP()}
        </div>
      </div>

      {/* ── Left side — hero ── */}
      <div className={styles.heroSide}>
        <div className={styles.heroOverlay}>
          <h1 className={styles.heroTitle}>תור דיגיטל</h1>
          <p className={styles.heroSubtitle}>
            ניהול תורים חכם לעסק שלך
          </p>
          <div className={styles.heroFeatures}>
            <div className={styles.heroFeature}>
              <span className={styles.featureIcon}>📅</span>
              <span>ניהול יומן ותורים</span>
            </div>
            <div className={styles.heroFeature}>
              <span className={styles.featureIcon}>👥</span>
              <span>ניהול לקוחות ושיעורים</span>
            </div>
            <div className={styles.heroFeature}>
              <span className={styles.featureIcon}>📊</span>
              <span>סטטיסטיקות ודוחות</span>
            </div>
            <div className={styles.heroFeature}>
              <span className={styles.featureIcon}>💳</span>
              <span>מנויים וכרטיסיות</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
